import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { invokeToolAction } from '@/lib/tool-action';
import { ToolActionConfirmationDialog } from './ToolActionConfirmationDialog';
import { ActionConnectionDialog } from './ActionConnectionDialog';
import {
  ACCOUNT_TASK_COMPLETED_EVENT,
  AccountCapabilityLauncher,
} from '@/components/activity/AccountCapabilityLauncher';
import {
  buildResultToolCreationPrompt,
  navigateToFlowComposerPrompt,
} from '@/lib/flow-composer-handoff';
import { navigateToToolWithPrefill } from '@/lib/tool-handoff';
import type { LightViewAction } from '@shared/types/views';
import type {
  ToolActionConfirmationEdit,
  ToolActionResponse,
} from '@shared/types/tool-actions';
import { getActionConnectionRequirements } from '@/lib/light-view-action';
import { toSafeUiMessage } from '@/lib/dashboard-errors';

type ToolHandoffViewAction = Extract<LightViewAction, { type: 'tool_handoff' }>;
type FlowHandoffViewAction = Extract<LightViewAction, { type: 'flow_handoff' }>;
type InlineCapabilityViewAction = Extract<LightViewAction, { type: 'inline_capability' }>;

interface ViewActionButtonProps {
  action: LightViewAction;
  variant?: 'primary' | 'secondary';
  className?: string;
  onToolHandoff?: (action: ToolHandoffViewAction) => void;
  onFlowHandoff?: (action: FlowHandoffViewAction) => void;
}

const LINK_STYLES = {
  primary:
    'border border-transparent bg-domino-aubergine text-white shadow-sm hover:bg-domino-prune',
  secondary:
    'border border-domino-ink bg-domino-white text-domino-ink shadow-sm hover:bg-domino-muted',
} as const;

export const ViewActionButton = ({
  action,
  variant = 'primary',
  className,
  onToolHandoff,
  onFlowHandoff,
}: ViewActionButtonProps) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [isRunningAction, setIsRunningAction] = useState(false);
  const [serverActionLabel, setServerActionLabel] = useState<string | null>(null);
  const [serverActionTitle, setServerActionTitle] = useState<string | null>(null);
  const [confirmationResponse, setConfirmationResponse] =
    useState<ToolActionResponse | null>(null);
  const [connectionResponse, setConnectionResponse] = useState<ToolActionResponse | null>(
    null
  );
  const [inlineCapabilityAction, setInlineCapabilityAction] =
    useState<InlineCapabilityViewAction | null>(null);
  const [inlineConnectionAction, setInlineConnectionAction] =
    useState<InlineCapabilityViewAction | null>(null);
  const [inlineConnectedKeys, setInlineConnectedKeys] = useState<Set<string>>(
    () => new Set()
  );

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
      return;
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  };

  if (action.type === 'link') {
    return (
      <a
        href={action.href}
        className={cn(
          'inline-flex h-8 items-center justify-center rounded-lg px-3 text-xs font-medium transition-colors',
          LINK_STYLES[variant],
          className
        )}
      >
        {action.label}
      </a>
    );
  }

  if (action.type === 'copy_text') {
    return (
      <Button
        size="sm"
        variant={variant}
        className={className}
        title="Copie le texte de l action dans le presse-papiers"
        onClick={() => copyToClipboard(action.text)}
      >
        {copied ? 'Copiee' : action.label}
      </Button>
    );
  }

  if (action.type === 'tool_handoff') {
    return (
      <Button
        size="sm"
        variant={variant}
        className={className}
        title={
          onToolHandoff
            ? 'Lance cette action depuis la page'
            : 'Ouvre l’outil avec le contexte préparé'
        }
        onClick={() => {
          if (onToolHandoff) {
            onToolHandoff(action);
            return;
          }

          navigateToToolWithPrefill(navigate, {
            toolKey: action.toolKey,
            prefill: action.prefill,
            ...(action.origin ? { origin: action.origin } : {}),
          });
        }}
      >
        {action.label}
      </Button>
    );
  }

  if (action.type === 'server_action') {
    const initialConnectionRequirements = getActionConnectionRequirements(action);
    const initialConnectionLabel =
      initialConnectionRequirements.length === 1
        ? `Connecter ${initialConnectionRequirements[0]?.label} et continuer`
        : initialConnectionRequirements.length > 1
          ? 'Connecter les services requis'
          : action.label;
    const runServerAction = async () => {
      if (isRunningAction) return;
      let preparedConfirmation = false;
      let preserveFeedback = false;
      setIsRunningAction(true);
      setServerActionLabel(action.pendingLabel ?? 'Action en cours...');
      setServerActionTitle(null);

      try {
        const response = await invokeToolAction({
          actionKey: action.actionKey,
          sourceRunId: action.sourceRunId,
          sourceActionId: action.sourceActionId ?? null,
          sourceActionIndex: action.sourceActionIndex ?? null,
          sourceActionLabel: action.sourceActionLabel ?? action.label,
          actionSpec: action.actionSpec ?? null,
          outcomeSpec: action.outcomeSpec ?? null,
        });

        setServerActionTitle(toSafeUiMessage(response.message, 'Action impossible.'));
        if (response.status !== 'needs_connection') {
          setConnectionResponse(null);
        }
        if (
          response.status === 'needs_confirmation' &&
          response.preview &&
          response.confirmationId
        ) {
          preparedConfirmation = true;
          setConfirmationResponse(response);
          setServerActionLabel('À confirmer');
        } else if (
          response.status === 'completed' ||
          response.status === 'partially_completed' ||
          response.status === 'already_done'
        ) {
          setServerActionLabel(action.successLabel ?? response.message);
          window.dispatchEvent(
            new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT, {
              detail: { sourceRunId: action.sourceRunId },
            })
          );
        } else if (response.status === 'needs_connection') {
          preserveFeedback = true;
          setServerActionLabel('Connexion requise');
          if ((response.connectionRequirements?.length ?? 0) > 0) {
            setConnectionResponse(response);
          }
        } else if (response.status === 'missing_info') {
          preserveFeedback = true;
          setServerActionLabel('Info manquante');
        } else {
          preserveFeedback = true;
          setServerActionLabel('Action impossible');
        }
      } catch (error) {
        preserveFeedback = true;
        setServerActionLabel('Action impossible');
        setServerActionTitle(
          toSafeUiMessage(
            error instanceof Error ? error.message : null,
            'Action impossible.'
          )
        );
      } finally {
        setIsRunningAction(false);
        if (!preparedConfirmation && !preserveFeedback) {
          window.setTimeout(() => {
            setServerActionLabel(null);
            setServerActionTitle(null);
          }, 3200);
        }
      }
    };

    const confirmServerAction = async (
      candidateIds: string[],
      confirmationEdits: ToolActionConfirmationEdit[]
    ) => {
      if (!confirmationResponse?.confirmationId || isRunningAction) return;
      setIsRunningAction(true);
      setServerActionLabel('Synchronisation...');
      try {
        const response = await invokeToolAction({
          actionKey: action.actionKey,
          sourceRunId: action.sourceRunId,
          sourceActionId: action.sourceActionId ?? null,
          sourceActionIndex: action.sourceActionIndex ?? null,
          sourceActionLabel: action.sourceActionLabel ?? action.label,
          actionSpec: action.actionSpec ?? null,
          outcomeSpec: action.outcomeSpec ?? null,
          confirmationId: confirmationResponse.confirmationId,
          confirmedCandidateIds: candidateIds,
          confirmationEdits,
        });
        setServerActionTitle(toSafeUiMessage(response.message, 'Action impossible.'));
        setServerActionLabel(
          response.status === 'completed' || response.status === 'partially_completed'
            ? (action.successLabel ?? response.message)
            : 'Action impossible'
        );
        if (
          response.status === 'completed' ||
          response.status === 'partially_completed'
        ) {
          setConfirmationResponse(null);
          window.dispatchEvent(
            new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT, {
              detail: { sourceRunId: action.sourceRunId },
            })
          );
        }
      } catch (error) {
        setServerActionLabel('Action impossible');
        setServerActionTitle(
          toSafeUiMessage(
            error instanceof Error ? error.message : null,
            'Action impossible.'
          )
        );
      } finally {
        setIsRunningAction(false);
        window.setTimeout(() => {
          setServerActionLabel(null);
          setServerActionTitle(null);
        }, 4200);
      }
    };

    return (
      <>
        <Button
          size="sm"
          variant={variant}
          className={className}
          title={serverActionTitle ?? 'Exécute cette action dans Domino'}
          disabled={isRunningAction}
          onClick={runServerAction}
        >
          {serverActionLabel ?? initialConnectionLabel}
        </Button>
        {serverActionTitle && !confirmationResponse && !isRunningAction && (
          <p
            role="status"
            className="mt-2 max-w-sm text-xs leading-5 text-domino-inkSoft"
          >
            {serverActionTitle}
          </p>
        )}
        <ToolActionConfirmationDialog
          response={confirmationResponse}
          executing={isRunningAction}
          onClose={() => {
            setConfirmationResponse(null);
            setServerActionLabel(null);
            setServerActionTitle(null);
          }}
          onConfirm={confirmServerAction}
        />
        {connectionResponse &&
          (connectionResponse.connectionRequirements?.length ?? 0) > 0 && (
            <ActionConnectionDialog
              actionLabel={action.label}
              message={connectionResponse.message}
              requirements={connectionResponse.connectionRequirements ?? []}
              onClose={() => {
                setConnectionResponse(null);
                setServerActionLabel(null);
                setServerActionTitle(null);
              }}
              onConnected={async () => runServerAction()}
            />
          )}
      </>
    );
  }

  if (action.type === 'inline_capability') {
    const canExecute = Boolean(action.sourceRunId && action.sourceActionLabel);
    const connectionRequirements = getActionConnectionRequirements(action);
    const connectionLabel = connectionRequirements.length === 1
      ? `Connecter ${connectionRequirements[0]?.label} et continuer`
      : connectionRequirements.length > 1
        ? 'Connecter les services requis'
        : action.label;
    return (
      <>
        <Button
          size="sm"
          variant={variant}
          className={className}
          disabled={!canExecute}
          title={
            canExecute
              ? 'Exécute cette action et enregistre le résultat dans le compte'
              : 'Cette ancienne action ne possède pas de référence d’exécution vérifiable'
          }
          onClick={() => {
            if (connectionRequirements.length > 0) {
              setInlineConnectedKeys(new Set());
              setInlineConnectionAction(action);
              return;
            }
            setInlineCapabilityAction(action);
          }}
        >
          {connectionLabel}
        </Button>
        <AccountCapabilityLauncher
          action={inlineCapabilityAction}
          onClose={() => setInlineCapabilityAction(null)}
        />
        {inlineConnectionAction && connectionRequirements.length > 0 && (
          <ActionConnectionDialog
            actionLabel={inlineConnectionAction.label}
            message="Connectez les sources nécessaires. La même action reprendra ensuite dans ce Thread."
            requirements={connectionRequirements.filter(
              (requirement) => !inlineConnectedKeys.has(requirement.connectorKey)
            )}
            onClose={() => {
              setInlineConnectionAction(null);
              setInlineConnectedKeys(new Set());
            }}
            onConnected={async (connectorKey) => {
              const next = new Set(inlineConnectedKeys);
              next.add(connectorKey);
              setInlineConnectedKeys(next);
              if (
                connectionRequirements.every((requirement) =>
                  next.has(requirement.connectorKey)
                )
              ) {
                setInlineConnectionAction(null);
                setInlineCapabilityAction(action);
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <Button
      size="sm"
      variant={variant}
      className={className}
      title={
        onFlowHandoff
          ? 'Propose de créer un outil dans la Forge'
          : 'Ouvre la suite recommandee'
      }
      onClick={() => {
        if (onFlowHandoff) {
          onFlowHandoff(action);
          return;
        }

        navigateToFlowComposerPrompt(
          navigate,
          buildResultToolCreationPrompt(action.seedPrompt)
        );
      }}
    >
      {action.label}
    </Button>
  );
};
